import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { DrillDownTableHeadProps } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import useTableHelper from "../useTableHelper";
import { TableHead, TableRow, TableCell } from "@mui/material";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const DrillDownTableHead = <RowType extends Record<string, any>, T extends Record<string, any>>({
  repeatProperties,
  hiddenColumns,
  loading,
  rowTextProperty,
  repeatColumnLength,
  showTotal,
}: React.PropsWithChildren<DrillDownTableHeadProps<RowType, T>>) => {
  const repeatPropertiesLength = repeatProperties?.properties ? Object.entries(repeatProperties.properties).length : 0;

  const repeatPropertiesList =
    repeatProperties &&
    Object.entries(repeatProperties.properties).filter(
      ([repeatKey, value]) => !hiddenColumns?.some((hc) => hc === repeatKey)
    );

  const { getNestedTableColumnTitle } = useTableHelper();

  return (
    <TableHead className={clsx(cssExports.tableHead)}>
      {repeatProperties?.filterField ? (
        <TableRow>
          <TableCell className={clsx(cssExports.tableCell, cssExports.emptyCell, cssExports.first)}></TableCell>
          <TableCell
            key={`tab-headerTop`}
            className={clsx(cssExports.tableCell, cssExports.freezeCell, cssExports.second)}
            colSpan={3}
          ></TableCell>
          <>
            {[...Array(repeatColumnLength)].map((col, repeatIndex) => (
              <TableCell
                key={`tab-repeat-headerTop-${repeatIndex}`}
                className={clsx(
                  cssExports.tableCell,
                  cssExports.repeatCellHeader,
                  showTotal || repeatIndex !== repeatColumnLength - 1 ? cssExports.repeatEnd : ""
                )}
                colSpan={Object.entries(repeatProperties.properties).length}
              >
                {repeatProperties.filterField && repeatProperties.filterField(repeatIndex, loading)}
              </TableCell>
            ))}
            {repeatProperties?.addRepeatColumnField && (
              <TableCell
                key={`tab-repeat-headerTop-new-${repeatColumnLength}`}
                className={clsx(cssExports.tableCell, cssExports.addRepeatCell)}
              >
                {repeatProperties.addRepeatColumnField && repeatProperties.addRepeatColumnField(repeatColumnLength)}
              </TableCell>
            )}
          </>
        </TableRow>
      ) : (
        <></>
      )}
      <TableRow className={cssExports.borderBottomRow}>
        <TableCell className={clsx(cssExports.tableCell, cssExports.emptyCell, cssExports.first)}></TableCell>

        <TableCell
          className={clsx(
            cssExports.tableCell,
            repeatProperties ? cssExports.freezeCell : "",
            cssExports.second,
            rowTextProperty.properties?.className
          )}
          align={rowTextProperty.properties?.alignment}
          style={{
            width: rowTextProperty.properties?.width,
          }}
          colSpan={3}
        >
          {rowTextProperty.properties?.description && getNestedTableColumnTitle(rowTextProperty.properties.description)}
        </TableCell>

        {repeatPropertiesList ? (
          [...Array(repeatColumnLength)].map((col, repeatIndex) => (
            <React.Fragment key={`repeat-columns-${repeatIndex}`}>
              {repeatPropertiesList.map(([repeatKey, value], cellIndex) => (
                <TableCell
                  key={`tab-repeat-header-${repeatIndex}-${cellIndex}`}
                  className={clsx(
                    cssExports.tableCell,
                    cellIndex === repeatPropertiesLength - 1 && (showTotal || repeatIndex !== repeatColumnLength - 1)
                      ? cssExports.repeatEnd
                      : ""
                  )}
                  align={value?.alignment}
                  style={{
                    width: value?.width,
                  }}
                >
                  {value?.description && getNestedTableColumnTitle(value.description, repeatIndex)}
                </TableCell>
              ))}
            </React.Fragment>
          ))
        ) : (
          <></>
        )}
        {showTotal ? (
          <TableCell
            key={`tab-repeat-header-total`}
            className={clsx(cssExports.tableCell, cssExports.totalCell)}
            align="right"
          >
            {`${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.InTotal)}`}
          </TableCell>
        ) : (
          <></>
        )}
        <TableCell
          key={`tab-repeat-headerTop-new-${repeatPropertiesLength}`}
          className={clsx(cssExports.tableCell, cssExports.addRepeatCell)}
        ></TableCell>
      </TableRow>
    </TableHead>
  );
};

export default DrillDownTableHead;
